import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EnSpinner from 'Components/feedback/EnSpinner/EnSpinner.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import { EnButton } from 'Components/base/EnButton';
import EnFormDialog from 'Components/modals/EnFormDialog/EnFormDialog.vue';
import { ExportLoadingState } from '../types/exportLoadingState';
import { dateFormatDay } from 'Js/utils/dateFormat';
import { exportDataToExcel } from 'Js/utils/exportUtils';
import { promiseForMinimumMs } from 'Js/utils/fetchUtils';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnExportAllDataDialog',
    props: /*@__PURE__*/ _mergeModels({
        loadItems: Function,
        headers: (Array),
        items: (Array),
        totalItems: Number,
        exportFileName: String,
        itemKey: String,
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['close'], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const { t } = useI18n();
        const model = _useModel(__props, "modelValue");
        // Controller for cancelling requests from Client. If Backend doesn't support it, request will still be processed by it. If Backend does handle CancellationToken, A lot of resources will be saved
        let abortController = new AbortController();
        /**
         * A dialog specifically for exporting data of a data grid.
         */
        const props = __props;
        const status = ref(ExportLoadingState.None);
        const isSuccess = computed(() => status.value === ExportLoadingState.Success);
        const isCanceled = computed(() => status.value === ExportLoadingState.Canceled);
        const isLoading = computed(() => status.value === ExportLoadingState.Loading);
        const hasError = computed(() => status.value === ExportLoadingState.Error);
        const hasTooManyRowsError = computed(() => status.value === ExportLoadingState.TooManyRows);
        const emit = __emit;
        let itemsToExport = [];
        watch(model, exportData, { deep: true });
        function close() {
            reset();
            emit('close');
        }
        function reset() {
            status.value = ExportLoadingState.None;
        }
        async function cancel() {
            if (abortController) {
                abortController.abort();
                status.value = ExportLoadingState.Canceled;
            }
        }
        function exportToExcel(externalItems) {
            const rowsToExport = [];
            const columnNames = [];
            let itemsForExport = externalItems;
            if (!itemsForExport)
                return;
            itemsForExport.forEach(_ => {
                rowsToExport.push({});
            });
            itemsForExport = externalItems ?? props.items?.filter(item => {
                return itemsForExport.includes(item[props.itemKey]);
            });
            const computedHeadersKeys = props.headers?.map(header => header.value);
            // To save headers order 
            props.headers?.forEach(header => {
                if (header.exportOptions && header.exportOptions.isIgnored)
                    return;
                // Because even some hidden columns still should be exported
                if ((!computedHeadersKeys?.includes(header.value) || header?.isHidden) && !header.exportOptions?.forcedExport)
                    return;
                let i = 0;
                columnNames.push({ label: header.exportOptions?.headerText ?? header.title, value: header.value, width: header.minWidth });
                itemsForExport.forEach(item => {
                    // If displayMapping exists, then apply it. 
                    // If no displayMapping function, apply data filter format
                    // else return raw value
                    let value;
                    if (header.displayMapping) {
                        value = header.displayMapping(item);
                    }
                    else {
                        // go through the chain of properties. if any property is an array, return values of all elements
                        const getValues = (item, propertyChain) => {
                            if (!item)
                                return null;
                            if (propertyChain.length === 0) {
                                return item;
                            }
                            let property = propertyChain.shift();
                            // remove '[]' from property if it's at the end
                            if (property.endsWith('[]')) {
                                property = property.slice(0, -2);
                            }
                            const result = [];
                            if (Array.isArray(item[property])) {
                                item[property].forEach((element) => {
                                    result.push(getValues(element, [...propertyChain]));
                                });
                            }
                            else {
                                result.push(getValues(item[property], propertyChain));
                            }
                            return result;
                        };
                        const itemValues = getValues(item, header.value.split('.'));
                        const itemValue = itemValues.length > 0 ? itemValues[0] : null;
                        value = header.displayMapping ?
                            header.displayMapping(item) :
                            itemValues.length > 1 ?
                                itemValues.join(', ') :
                                header.exportOptions?.formatData ?
                                    header.exportOptions.formatData(itemValue) :
                                    itemValue;
                        if (Array.isArray(value)) {
                            value = value.join(', ');
                        }
                    }
                    // if value is array, join it to a string
                    rowsToExport[i][header.value] = value;
                    i++;
                });
            });
            let fileName = `${props.exportFileName}_${dateFormatDay(new Date(), true)}`;
            exportDataToExcel(rowsToExport, fileName, columnNames);
        }
        async function exportData(load) {
            if ((props.totalItems ?? 0) > 50000) {
                status.value = ExportLoadingState.TooManyRows;
                return;
            }
            if (!load)
                return;
            abortController = new AbortController();
            status.value = ExportLoadingState.Loading;
            const signal = abortController.signal;
            itemsToExport = await props.loadItems?.(true, signal);
            if (itemsToExport) {
                exportToExcel(itemsToExport);
                status.value = ExportLoadingState.Success;
            }
            else if (status.value !== ExportLoadingState.Canceled) {
                status.value = ExportLoadingState.Error;
            }
            abortController = null;
        }
        async function downloadExport() {
            status.value = ExportLoadingState.Loading;
            abortController = new AbortController();
            const signal = abortController.signal;
            try {
                await promiseForMinimumMs();
                if (signal.aborted) {
                    status.value = ExportLoadingState.Canceled;
                    return;
                }
                exportToExcel(itemsToExport);
                if (!signal.aborted) {
                    status.value = ExportLoadingState.Success;
                }
            }
            catch (error) {
                if (signal.aborted) {
                    status.value = ExportLoadingState.Canceled;
                }
                else {
                    status.value = ExportLoadingState.Error;
                }
            }
        }
        const __returned__ = { t, model, get abortController() { return abortController; }, set abortController(v) { abortController = v; }, props, status, isSuccess, isCanceled, isLoading, hasError, hasTooManyRowsError, emit, get itemsToExport() { return itemsToExport; }, set itemsToExport(v) { itemsToExport = v; }, close, reset, cancel, exportToExcel, exportData, downloadExport, EnSpinner, EnIcon, get EnButton() { return EnButton; }, EnFormDialog };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
